// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/AddProduct";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import Accordion from "@insite/mobius/Accordion";
import { AccordionPresentationProps } from "@insite/mobius/Accordion/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "@toolkit/Framework/Common/Utilities/parseFullPageHtml";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    quickOrderMessage = "quickOrderMessage",
    quickOrderTitle = "quickOrderTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.quickOrderTitle]: string;
        [fields.quickOrderMessage]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    isGuest: state.context.session?.isGuest,
});

const mapDispatchToProps = {
    addProduct,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface QuickOrderSearchStyles {
    productSelector?: ProductSelectorStyles;
    wrapper?: InjectableCss;
    container?: InjectableCss;
    accordionStyles?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    quickOrderMessageText?: TypographyPresentationProps;
}

export const quickOrderSearchStyles: QuickOrderSearchStyles = {
    wrapper: {},
    container: {
        css: css`
            padding: ${x => x.theme.design.layout.spacing.content};
            padding-top: 0;
        `,
    },
    accordionSection: {
        titleTypographyProps: {
            css: css`
                ${({ theme }) => `
                    ${theme.accordion.sectionDefaultProps?.css};
                    font-family: ${theme.design.typography.fontFamily.title}
                `}
            `,
        },
    },
    quickOrderMessageText: {
        css: css`
            display: inline-block;
            ${({ theme }) => `
                margin-bottom: ${theme.design.typography.spacing.base};
                font-size: ${theme.design.typography.size.smaller};
            `}
        `,
    },
};

const styles = quickOrderSearchStyles;

const QuickOrderSearch = ({ fields: { quickOrderMessage, quickOrderTitle }, addProduct, isGuest }: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [quickOrderOpen, setQuickOrederOpen] = useState(false);
    const { isInShell } = useShellContext();

    const addProductToOrder = (productInfo: ProductInfo, product: ProductModel) => {
        addProduct({ productInfo, product });
        toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
    };

    const toggleQuickOrderOpen = () => {
        setQuickOrederOpen(!quickOrderOpen);
    };

    if (isGuest) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Accordion headingLevel={2}>
                <AccordionSection
                    title={quickOrderTitle}
                    expanded={quickOrderOpen}
                    onTogglePanel={toggleQuickOrderOpen}
                    {...styles.accordionSection}
                >
                    <StyledWrapper {...styles.container}>
                        <Typography {...styles.quickOrderMessageText}>
                            {parse(quickOrderMessage, parserOptions)}
                        </Typography>
                        <ProductSelector
                            selectButtonTitle={translate("Add to Cart")}
                            onSelectProduct={addProductToOrder}
                            productIsConfigurableMessage={siteMessage("QuickOrder_CannotOrderConfigurable")}
                            productIsUnavailableMessage={siteMessage("QuickOrder_ProductIsUnavailable")}
                            extendedStyles={styles.productSelector}
                        />
                    </StyledWrapper>
                </AccordionSection>
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(QuickOrderSearch),
    definition: {
        displayName: "Quick Order Reminder",
        group: "Cart",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.quickOrderTitle,
                displayName: "Quick Order Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Got everything you need?",
            },
            {
                name: fields.quickOrderMessage,
                displayName: "Quick Order Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Use the form below to quickly add any additionnal items to your cart.",
            },
        ],
    },
};

export default widgetModule;
